/**
 * Buildings render pass — instanced unit cubes, one per Building written by
 * gen-buildings. Geometry is hardcoded in the vertex shader (36 vertices, no
 * index buffer); per-instance attributes come from `gen.buildings.instanceBuffer`
 * bound as a vertex buffer with stepMode='instance'.
 *
 * Camera is at group(0); group(1) is empty (no per-pass bindings); daynight at
 * group(2). The instance count lives in `instanceCountBuffer` (u32 at offset 0,
 * written by the gen compute shader); we copy that single u32 into the
 * `instanceCount` field of an indirect draw struct once at startup, so per-frame
 * draws stay GPU-side via `drawIndirect`.
 */
import { createRenderPipeline } from '../gpu/pipelines.ts';
import { createEmptyBindGroup } from '../gpu/buffers.ts';
import cubeWgsl from './cube.wgsl?raw';
import shadowPcfWgsl from './shadow_pcf.wgsl?raw';
import fogWgsl from './fog.wgsl?raw';
import buildingsWgsl from './buildings.wgsl?raw';

const wgsl = cubeWgsl + '\n' + buildingsWgsl + '\n' + shadowPcfWgsl + '\n' + fogWgsl;

export interface BuildingsPass {
  draw(pass: GPURenderPassEncoder): void;
  destroy(): void;
}

export interface BuildingsPassOptions {
  presentationFormat: GPUTextureFormat;
  depthFormat: GPUTextureFormat;
  instanceBuffer: GPUBuffer;
  instanceCountBuffer: GPUBuffer;
  maxInstances: number;
  cameraBindGroupLayout: GPUBindGroupLayout;
  dayNightBindGroupLayout: GPUBindGroupLayout;
  shadowBindGroupLayout: GPUBindGroupLayout;
}

const INSTANCE_STRIDE = 32;
const CUBE_VERTEX_COUNT = 36;
const INDIRECT_DRAW_BYTES = 16;

export function createBuildingsPass(device: GPUDevice, opts: BuildingsPassOptions): BuildingsPass {
  // Empty group(1) so the pipeline layout still uses three slots and matches
  // the camera/daynight indices used by the frame renderer.
  const empty = createEmptyBindGroup(device, 'buildings');

  const pipelineLayout = device.createPipelineLayout({
    label: 'buildings.pll',
    bindGroupLayouts: [
      opts.cameraBindGroupLayout,
      empty.layout,
      opts.dayNightBindGroupLayout,
      opts.shadowBindGroupLayout,
    ],
  });

  const pipeline = createRenderPipeline(device, wgsl, {
    label: 'buildings',
    layout: pipelineLayout,
    buffers: [{
      arrayStride: INSTANCE_STRIDE,
      stepMode: 'instance',
      attributes: [
        { shaderLocation: 0, offset: 0,  format: 'float32x3' }, // pos
        { shaderLocation: 1, offset: 12, format: 'float32x2' }, // footprint
        { shaderLocation: 2, offset: 20, format: 'float32' },   // height
        { shaderLocation: 3, offset: 24, format: 'uint32' },    // styleId
        { shaderLocation: 4, offset: 28, format: 'uint32' },    // flags (rotation + roof + tint)
      ],
    }],
    targets: [{ format: opts.presentationFormat }],
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
    },
  });

  // Indirect draw buffer: { vertexCount, instanceCount, firstVertex, firstInstance }.
  // vertexCount fixed at 36, firstVertex/firstInstance at 0; instanceCount comes from
  // gen-buildings' atomic counter via copyBufferToBuffer.
  const indirectBuffer = device.createBuffer({
    label: 'buildings.indirect',
    size: INDIRECT_DRAW_BYTES,
    usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(indirectBuffer, 0, new Uint32Array([CUBE_VERTEX_COUNT, 0, 0, 0]));

  // Copy the u32 instance count from the gen counter into the indirect struct's
  // instanceCount field (offset 4). One-shot at startup — the count is fixed once
  // gen-buildings has run.
  const enc = device.createCommandEncoder({ label: 'buildings.indirect.populate' });
  enc.copyBufferToBuffer(opts.instanceCountBuffer, 0, indirectBuffer, 4, 4);
  device.queue.submit([enc.finish()]);

  return {
    draw(pass) {
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, empty.bindGroup);
      pass.setVertexBuffer(0, opts.instanceBuffer);
      pass.drawIndirect(indirectBuffer, 0);
    },
    destroy() {
      indirectBuffer.destroy();
    },
  };
}
